import * as i18n from 'i18next';

import { SERVER_ERROR_CODES } from '@/consts/server-error-codes.consts';
import { I18N_KEYS } from '@/language/keys';
import { log } from '@/logger/logger';

const logger = log.getLogger('firebase-errors');

export const INTERNAL_AUTH_ERROR_CODES = {
  OIDC_MISSING_CODE: 'auth-internal/oidc-missing-code',
  OIDC_MISSING_STATE: 'auth-internal/oidc-missing-state',
  OIDC_STATE_MISMATCH: 'auth-internal/oidc-state-mismatch',
  OIDC_MISSING_NONCE: 'auth-internal/oidc-missing-nonce',
  UNSUPPORTED_PROVIDER: 'auth-internal/unsupported-provider',
  OIDC_SIGN_IN_WITH_FIGMA_TOKEN_FAILED: 'auth-internal/oidc-sign-in-with-figma-token-failed',
};

export class InternalAuthError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'InternalAuthError';
    this.code = code;
  }
}
/**
 * Checks if an error is a Firebase Auth error
 */
export const isFirebaseAuthError = (error: any): boolean => {
  return error?.code?.startsWith('auth/') || false;
};

/**
 * Parses Firebase Auth error codes and returns user-friendly error messages
 */
export const getFirebaseAuthErrorMessage = (error: any): string => {
  // Check if it's a Firebase Auth error
  if (!error || !isFirebaseAuthError(error)) {
    // handle specific server error codes here
    if (error?.response?.data?.internalErrorCode === SERVER_ERROR_CODES.EMAIL_DOMAIN_UNAUTHORIZED_TO_SIGN_IN) {
      return i18n.t(I18N_KEYS.SIGN_IN_PAGE.EMAIL_DOMAIN_UNAUTHORIZED);
    }
    if (error?.response?.data?.internalErrorCode === SERVER_ERROR_CODES.EMAIL_PASSWORD_LOGIN_NOT_ALLOWED) {
      return i18n.t(I18N_KEYS.SIGN_IN_PAGE.EMAIL_PASSWORD_LOGIN_NOT_ALLOWED);
    }
    // If there's a custom error message (like from team invitation), use it
    if (error?.message && !error?.code?.startsWith('auth-internal/')) {
      return error.message;
    }

    // log the raw error
    logger.error('Auth internal error:', error);
    // Return generic message if no specific message available
    return 'An unexpected error occurred. Please try again.';
  }

  // Handle specific Firebase Auth error codes
  switch (error.code) {
    case 'auth/popup-closed-by-user':
      return 'Sign-in cancelled';

    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with the same email address but different sign-in credentials';

    case 'auth/invalid-credential':
      return 'Invalid credentials. Please try again';

    case 'auth/operation-not-allowed':
      return 'This sign-in method is not enabled';

    case 'auth/user-disabled':
      return 'This account has been disabled';

    case 'auth/user-not-found':
      return 'No account found with this email';

    case 'auth/wrong-password':
      return 'Incorrect password';

    case 'auth/invalid-email':
      return 'Invalid email address';

    case 'auth/email-already-in-use':
      return 'This email is already registered';

    case 'auth/weak-password':
      return 'Password is too weak';

    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again';

    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later';

    case 'auth/popup-blocked':
      return 'Sign-in popup was blocked. Please allow popups for this site';

    default:
      // If there's a custom error message, use it
      if (error.message) {
        return error.message;
      }

      // Fallback to generic error message
      return 'Failed to sign in. Please try again.';
  }
};
